import { FC, MouseEvent, useCallback, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { isEmpty } from 'lodash';
import { useSearchParams } from 'react-router-dom';
import { firmDailyNavSettingsUrl } from 'common/config/urls';
import { selectDailyNavApplyDates, selectDailyNavDayConfig } from 'common/constants/dailyNav';
import { useFirmScopedPermissions } from 'common/hooks';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { CustomScalarSpreadsheet, MessageBox } from 'components';
import { GridSkeleton } from 'components/Grid';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import { PrimaryNavigationMenu, PrimaryNavigationMenuItem } from 'components/ui';
import { useTertiaryNavigationRegistration } from 'context/FirmBaseLayoutContext';
import type { TertiaryNavigationActionControlConfig } from 'context/FirmBaseLayoutContext';
import { useGetDailyNavConfigApplyPreview } from 'services/hooks/dailyNav';
import {
  useGenerateDailyNavRecords,
  useGetCompanyListByFirm,
  useGetDailyNavPreviousValuations,
  useGetDailyNavRecords,
} from 'services/hooks/firms';
import { getArrayValue, getBooleanValue, getObjectValue } from 'utilities';
import DayConfigBanner from './components/DayConfigBanner';
import DeltaBreakdownDialog from './components/DeltaBreakdownDialog';
import FirmRulesIcon from './components/FirmRulesIcon';
import ReportDateSelector from './components/ReportDateSelector';
import ReportDialog from './components/ReportDialog';
import ReviewPriceDialog from './components/ReviewPriceDialog';
import SecondaryBreakdownDialog from './components/SecondaryBreakdownDialog';
import { DailyNavRecordWithDay, FirmDailyNavDay } from './daily-nav.api.types';
import {
  DAILY_NAV_REPORT_DATE_PARAM,
  DAILY_NAV_REPORT_PHASES,
  DailyNavReportTab,
} from './daily-nav.constants';
import { DailyNavTableRow, useDailyNavSheet } from './hooks/useDailyNavSheet';

const DailyNav: FC = () => {
  const [storeValue] = useStore() as UseStoreValues;
  const { firmInfo, user } = getObjectValue(storeValue);
  const { id: firmId, slug: firmSlug } = getObjectValue(firmInfo);
  const isStaffOrSuperuser = getBooleanValue(user?.is_staff) || getBooleanValue(user?.is_superuser);
  const { canMutateFirmScopedEntities } = useFirmScopedPermissions({ firmId });
  const canReviewPrice = canMutateFirmScopedEntities || isStaffOrSuperuser;

  const [reviewRow, setReviewRow] = useState<DailyNavTableRow | null>(null);
  const [breakdownRow, setBreakdownRow] = useState<DailyNavTableRow | null>(null);
  const [secondaryBreakdownRow, setSecondaryBreakdownRow] = useState<DailyNavTableRow | null>(null);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [reportTab, setReportTab] = useState<DailyNavReportTab | undefined>(undefined);
  const [reportMenuAnchorEl, setReportMenuAnchorEl] = useState<HTMLElement | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDate = searchParams.get(DAILY_NAV_REPORT_DATE_PARAM) ?? undefined;

  const setSelectedDate = useCallback(
    (date: string) => {
      setSearchParams(
        previous => {
          const next = new URLSearchParams(previous);
          next.set(DAILY_NAV_REPORT_DATE_PARAM, date);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const { mutate: generateDailyNavRecords, isPending: isGenerating } = useGenerateDailyNavRecords(firmId);
  const { data: companies } = useGetCompanyListByFirm({ firmId });
  const { data: dailyNavRecords, isLoading: isDailyNavRecordsLoading } = useGetDailyNavRecords({
    firmId,
    date: selectedDate,
  });

  const resolvedDate = dailyNavRecords?.[0]?.date;
  const reportDate = selectedDate ?? resolvedDate;

  const { data: previousValuations } = useGetDailyNavPreviousValuations({
    firmId,
    date: reportDate,
    fields: ['equity_value', 'percent_change_from_previous', 'common_share_value', 'cap_table_currency'],
    shouldQueryAutomatically: Boolean(firmId) && Boolean(reportDate),
  });

  const { data: applyPreview } = useGetDailyNavConfigApplyPreview(firmId);
  const isOpenNavDay = useMemo(
    () =>
      Boolean(
        reportDate &&
          selectDailyNavApplyDates(applyPreview?.dates).applicableDates.some(date => date.nav_date === reportDate)
      ),
    [applyPreview?.dates, reportDate]
  );
  const { config: dayConfig, isStale, unanchoredCount } = useMemo(
    () => selectDailyNavDayConfig(dailyNavRecords, isOpenNavDay),
    [dailyNavRecords, isOpenNavDay]
  );

  const leftActionsNode = useMemo(
    () => (
      <Box display="flex" alignItems="center" gap={1}>
        <ReportDateSelector value={reportDate} onChange={setSelectedDate} />
        <FirmRulesIcon config={dayConfig} settingsUrl={firmSlug ? firmDailyNavSettingsUrl(firmSlug) : undefined} />
      </Box>
    ),
    [reportDate, setSelectedDate, dayConfig, firmSlug]
  );

  const canGenerateRecords = isStaffOrSuperuser && !isDailyNavRecordsLoading && isEmpty(dailyNavRecords);

  const day = useMemo<FirmDailyNavDay | null>(() => {
    const fromRecord = (dailyNavRecords ?? [])
      .map(record => (record as DailyNavRecordWithDay).day)
      .find(candidate => candidate != null);
    return fromRecord ?? null;
  }, [dailyNavRecords]);
  const isDayFinalized = Boolean(day?.is_finalized);
  const isDaySent = Boolean(day?.is_sent);
  let reportButtonLabel = 'Report';
  if (isDaySent) {
    reportButtonLabel = 'Sent';
  } else if (isDayFinalized) {
    reportButtonLabel = 'Finalized';
  }
  const isReportDisabled = !firmId || isEmpty(dailyNavRecords);

  const openReport = useCallback((tab?: DailyNavReportTab) => {
    setReportTab(tab);
    setIsReportOpen(true);
    setReportMenuAnchorEl(null);
  }, []);

  const handleReportMenuClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setReportMenuAnchorEl(event.currentTarget);
  }, []);

  const tertiaryNavActions = useMemo<TertiaryNavigationActionControlConfig[]>(() => {
    const actions: TertiaryNavigationActionControlConfig[] = [];
    if (canReviewPrice || isDayFinalized || isDaySent) {
      actions.push({
        type: 'splitButton',
        label: reportButtonLabel,
        variant: 'primary',
        disabled: isReportDisabled,
        testId: 'finalize-daily-nav-button',
        menuAriaLabel: 'Open report phases',
        menuDisabled: isReportDisabled,
        onClick: () => openReport(),
        onMenuClick: handleReportMenuClick,
      });
    }
    if (canGenerateRecords) {
      actions.push({
        type: 'button',
        label: 'Generate Daily NAVs',
        variant: 'primary',
        disabled: isGenerating || !firmId,
        testId: 'generate-daily-nav-records-button',
        onClick: () => generateDailyNavRecords(selectedDate),
      });
    }
    return actions;
  }, [
    canGenerateRecords,
    canReviewPrice,
    firmId,
    generateDailyNavRecords,
    handleReportMenuClick,
    isDayFinalized,
    isDaySent,
    isGenerating,
    isReportDisabled,
    openReport,
    reportButtonLabel,
    selectedDate,
  ]);

  const tertiaryNavigation = useMemo(
    () => ({
      ariaLabel: 'Daily NAV controls',
      actions: tertiaryNavActions,
      leftActionsNode,
    }),
    [leftActionsNode, tertiaryNavActions]
  );

  useTertiaryNavigationRegistration(tertiaryNavigation);

  const { spreadsheet } = useDailyNavSheet({
    companies,
    firmSlug,
    records: dailyNavRecords,
    previousValuations,
    onReviewClick: setReviewRow,
    canReview: canReviewPrice,
    onBreakdownClick: setBreakdownRow,
    onSecondaryBreakdownClick: setSecondaryBreakdownRow,
  });

  const spreadsheets = useMemo(() => (spreadsheet?.rowConfig?.length ? [spreadsheet] : []), [spreadsheet]);
  const { onChange, workbook, workbookContextValue } = useWorkbook(spreadsheets);

  const sheetProps = useMemo(() => {
    if (!spreadsheet) {
      return null;
    }
    return {
      ...spreadsheet,
      data: getArrayValue(spreadsheet.data),
      onChange,
      sheet: spreadsheet,
      workbook,
    } as unknown as typeof ScalarSpreadsheet;
  }, [spreadsheet, onChange, workbook]);

  return (
    <Box className="daily-nav-container">
      {!isDailyNavRecordsLoading && (
        <DayConfigBanner
          config={dayConfig}
          isStale={isStale}
          unanchoredCount={unanchoredCount}
          firmSettingsUrl={firmSlug ? firmDailyNavSettingsUrl(firmSlug) : undefined}
        />
      )}

      {isDailyNavRecordsLoading && <GridSkeleton maxColumns={10} maxRows={4} />}

      {!isDailyNavRecordsLoading && isEmpty(dailyNavRecords) && (
        <MessageBox
          fullWidth={false}
          title="Daily NAV"
          tagline={
            isStaffOrSuperuser
              ? 'No Daily NAV records yet for this date. Records are generated automatically each trading day; use Generate Daily NAVs to create them now.'
              : 'No Daily NAV records yet for this date. Records are generated automatically each trading day.'
          }
        />
      )}

      {sheetProps && (
        <WorkbookContext.Provider value={workbookContextValue}>
          <CustomScalarSpreadsheet {...sheetProps} isLoading={isDailyNavRecordsLoading} />
        </WorkbookContext.Provider>
      )}

      <PrimaryNavigationMenu
        anchorEl={reportMenuAnchorEl}
        menuMinWidth={180}
        menuWidth="content"
        onClose={() => setReportMenuAnchorEl(null)}
        open={Boolean(reportMenuAnchorEl)}
        data-testid="daily-nav-report-phase-menu">
        {DAILY_NAV_REPORT_PHASES.map(phase => (
          <PrimaryNavigationMenuItem
            key={phase.tab}
            data-testid={`daily-nav-report-menu-${phase.tab}`}
            onClick={() => openReport(phase.tab)}>
            {phase.label}
          </PrimaryNavigationMenuItem>
        ))}
      </PrimaryNavigationMenu>
      <ReportDialog
        open={isReportOpen}
        onClose={() => setIsReportOpen(false)}
        firmId={firmId}
        date={reportDate}
        day={day}
        initialTab={reportTab}
        isStaff={isStaffOrSuperuser}
      />
      <ReviewPriceDialog row={reviewRow} open={Boolean(reviewRow)} onClose={() => setReviewRow(null)} />
      <DeltaBreakdownDialog row={breakdownRow} open={Boolean(breakdownRow)} onClose={() => setBreakdownRow(null)} />
      <SecondaryBreakdownDialog
        row={secondaryBreakdownRow}
        open={Boolean(secondaryBreakdownRow)}
        onClose={() => setSecondaryBreakdownRow(null)}
      />
    </Box>
  );
};

export default DailyNav;
